import React, { useState, useEffect } from 'react';
import {
  BatteryLow,
  BatteryMedium,
  BatteryFull,
  Brain,
  Palette,
  Heart,
  Users,
  Footprints,
  Moon,
  Sofa,
  Monitor,
  Smile,
  Meh,
  Frown,
  SmilePlus,
  Annoyed,
  ChevronDown,
  ChevronUp,
  Sparkles
} from 'lucide-react';
import { ABLE_COLORS } from '../../constants';
import MoodModal from './MoodModal';
import StateModal from './StateModal';

// Icon size constant for consistency
const ICON_SIZE = 24;

// Mood icon scale (replacing emojis)
const MOOD_ICONS = [
  { level: 1, icon: Frown },      // Rough
  { level: 2, icon: Annoyed },    // Meh
  { level: 3, icon: Meh },        // Okay
  { level: 4, icon: Smile },      // Good
  { level: 5, icon: SmilePlus }   // Great
];

// Energy options
const ENERGY_OPTIONS = [
  { value: 'Low', icon: BatteryLow, color: ABLE_COLORS.energyLevel.low },
  { value: 'Med', icon: BatteryMedium, color: ABLE_COLORS.energyLevel.med },
  { value: 'High', icon: BatteryFull, color: ABLE_COLORS.energyLevel.high }
];

// Focus options (renamed from Mode)
const FOCUS_OPTIONS = [
  { value: 'Creative', label: 'Creative', icon: Palette, color: ABLE_COLORS.state.creative },
  { value: 'Thinking', label: 'Thinking', icon: Brain, color: ABLE_COLORS.state.mental },
  { value: 'Wellbeing', label: 'Wellbeing', icon: Heart, color: ABLE_COLORS.state.emotional },
  { value: 'Social', label: 'Social', icon: Users, color: ABLE_COLORS.state.social },
  { value: 'Physical', label: 'Physical', icon: Footprints, color: ABLE_COLORS.state.physical },
  { value: 'Rest', label: 'Rest', icon: Moon, color: ABLE_COLORS.state.rest }
];

// Space options (renamed from Motion)
const SPACE_OPTIONS = [
  { value: 'Couch', label: 'Couch', icon: Sofa, color: ABLE_COLORS.location.couch },
  { value: 'Desk', label: 'Desk', icon: Monitor, color: ABLE_COLORS.location.desk },
  { value: 'Moving', label: 'Moving', icon: Footprints, color: ABLE_COLORS.location.moving }
];

/**
 * StateCard - Individual mini card for state display
 * Neutral background with colored icon - state recedes after input
 */
function StateCard({ icon, label, color, isSet, onClick }) {
  const hasValue = isSet && icon;
  
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: '12px 8px',
        background: ABLE_COLORS.neutral,
        border: 'none',
        borderRadius: ABLE_COLORS.radiusMedium,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
        boxShadow: ABLE_COLORS.cardShadow,
        transition: 'all 0.15s ease',
        minWidth: 0
      }}
    >
      {icon ? (
        React.createElement(icon, { 
          size: ICON_SIZE, 
          color: hasValue ? color : ABLE_COLORS.textMuted,
          strokeWidth: 2
        })
      ) : (
        <span style={{ 
          fontSize: `${ICON_SIZE}px`, 
          lineHeight: 1, 
          color: ABLE_COLORS.textMuted 
        }}>?</span>
      )}
      <span style={{
        fontSize: '11px',
        fontWeight: '600',
        color: hasValue ? ABLE_COLORS.textPrimary : ABLE_COLORS.textMuted,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%'
      }}>
        {label || 'Tap'}
      </span>
    </button>
  );
}

const STORAGE_KEY = 'mabel-state-cards-expanded';
const FIRST_VISIT_KEY = 'mabel-state-cards-first-visit';

// Check if timestamp is from today
function isFromToday(timestamp) {
  if (!timestamp) return false;
  const date = new Date(timestamp);
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

/**
 * StateCards - Row of 4 state mini cards
 * Expanded on first visit, then respects user preference
 * Shows pulse indicator when state needs check-in
 */
export default function StateCards({ currentState, onStateChange, onToast }) {
  const [activeModal, setActiveModal] = useState(null);
  const [isExpanded, setIsExpanded] = useState(() => {
    // Check if this is first visit
    const hasVisited = localStorage.getItem(FIRST_VISIT_KEY);
    if (!hasVisited) {
      // First visit - expand by default and mark as visited
      localStorage.setItem(FIRST_VISIT_KEY, 'true');
      return true;
    }
    // Subsequent visits - respect stored preference
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'true';
  });

  // Persist expansion state
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, isExpanded.toString());
  }, [isExpanded]);

  // Get mood display
  const getMoodDisplay = () => {
    const mood = currentState.mood;
    if (!mood) {
      // Backwards compatibility
      if (currentState.emotion) {
        const emotionMap = { 'Rough': 1, 'Meh': 2, 'Okay': 3, 'Good': 4, 'Great': 5 };
        const level = emotionMap[currentState.emotion] || 3;
        const moodIcon = MOOD_ICONS.find(m => m.level === level)?.icon || Meh;
        return { icon: moodIcon, label: currentState.emotion, color: ABLE_COLORS.mood[level], isSet: true };
      }
      return { icon: Smile, label: 'Mood', color: '#9CA3AF', isSet: false };
    }
    
    const moodIcon = MOOD_ICONS.find(m => m.level === mood.level)?.icon || Meh;
    // Show first descriptor, or friendly level name as fallback
    const levelNames = { 1: 'Rough', 2: 'Meh', 3: 'Okay', 4: 'Good', 5: 'Great' };
    const label = (mood.descriptors && mood.descriptors.length > 0) 
      ? mood.descriptors[0] 
      : levelNames[mood.level] || 'Mood';
    
    // Color based on level
    const color = ABLE_COLORS.mood[mood.level] || ABLE_COLORS.mood[3];
    
    return { icon: moodIcon, label, color, isSet: true };
  };

  // Get energy display
  const getEnergyDisplay = () => {
    const energy = currentState.energy;
    if (!energy) {
      return { icon: BatteryMedium, label: 'Energy', color: '#9CA3AF', isSet: false };
    }
    const opt = ENERGY_OPTIONS.find(o => o.value === energy);
    return { 
      icon: opt?.icon || BatteryMedium, 
      label: energy, 
      color: opt?.color || '#9CA3AF',
      isSet: true 
    };
  };

  // Get focus display (renamed from mode)
  // Handles multiple field names for backwards compatibility: intents, intent, focus
  const getFocusDisplay = () => {
    // Build intents array from various possible field names
    let intents = [];
    if (Array.isArray(currentState.intents) && currentState.intents.length > 0) {
      intents = currentState.intents;
    } else if (currentState.intent && typeof currentState.intent === 'string') {
      intents = [currentState.intent];
    } else if (currentState.focus && typeof currentState.focus === 'string') {
      // Old field name from initial state
      intents = [currentState.focus];
    }

    // Filter out null/undefined/empty values
    intents = intents.filter(i => i && typeof i === 'string' && i.trim() !== '');

    if (intents.length === 0) {
      return { icon: Brain, label: 'Focus', color: '#9CA3AF', isSet: false };
    }

    const first = FOCUS_OPTIONS.find(o => o.value === intents[0]);
    const label = intents.length > 1
      ? `${first?.label || intents[0]} +${intents.length - 1}`
      : (first?.label || intents[0]);

    return {
      icon: first?.icon || Brain,
      label,
      color: first?.color || '#9CA3AF',
      isSet: true
    };
  };

  // Get space display (renamed from motion)
  const getSpaceDisplay = () => {
    const location = currentState.location;
    if (!location || location.startsWith('Other')) {
      return { icon: Sofa, label: 'Space', color: '#9CA3AF', isSet: false };
    }
    const opt = SPACE_OPTIONS.find(o => o.value === location);
    return { 
      icon: opt?.icon || Sofa, 
      label: location, 
      color: opt?.color || '#9CA3AF',
      isSet: true 
    };
  };

  const moodDisplay = getMoodDisplay();
  const energyDisplay = getEnergyDisplay();
  const focusDisplay = getFocusDisplay();
  const spaceDisplay = getSpaceDisplay();

  // Check if state needs a check-in (any value missing OR not updated today)
  const needsCheckIn = !isFromToday(currentState.timestamp) ||
    !moodDisplay.isSet || !energyDisplay.isSet || !focusDisplay.isSet || !spaceDisplay.isSet;

  // Handle state updates
  const handleMoodChange = (mood) => {
    const newState = {
      ...currentState,
      mood: mood || undefined, // Clear if null
      // Clear old fields
      emotion: undefined,
      feeling: undefined,
      timestamp: new Date().toISOString()
    };
    onStateChange(newState);
    setActiveModal(null);
    if (onToast) {
      onToast(mood ? 'Got it ✓' : 'Cleared ✓');
    }
  };

  const handleEnergyChange = (energy) => {
    const newState = {
      ...currentState,
      energy: energy || undefined, // Clear if null
      timestamp: new Date().toISOString()
    };
    onStateChange(newState);
    setActiveModal(null);
    if (onToast) onToast(energy ? 'Got it ✓' : 'Cleared ✓');
  };

  const handleFocusChange = (intents) => {
    const hasIntents = Array.isArray(intents) && intents.length > 0;
    const newState = {
      ...currentState,
      intents: hasIntents ? intents : undefined, // Clear if empty
      intent: undefined,
      timestamp: new Date().toISOString()
    };
    onStateChange(newState);
    setActiveModal(null);
    if (onToast) onToast(hasIntents ? 'Got it ✓' : 'Cleared ✓');
  };

  const handleSpaceChange = (location) => {
    const newState = {
      ...currentState,
      location: location || undefined, // Clear if null
      timestamp: new Date().toISOString()
    };
    onStateChange(newState);
    setActiveModal(null);
    if (onToast) onToast(location ? 'Got it ✓' : 'Cleared ✓');
  };

  return (
    <>
      {/* State Container - Collapsible */}
      <div style={{
        marginBottom: '16px',
        position: 'relative',
        background: ABLE_COLORS.neutral,
        borderRadius: ABLE_COLORS.cardRadius,
        boxShadow: ABLE_COLORS.cardShadow,
        overflow: 'hidden'
      }}>
        {/* Clickable header */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '14px 16px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left'
          }}
        >
          <Sparkles size={20} color={ABLE_COLORS.accent} />
          <span style={{
            fontSize: '14px',
            fontWeight: '600',
            color: ABLE_COLORS.textPrimary,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            My State
            {needsCheckIn && !isExpanded && (
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: ABLE_COLORS.accent,
                  animation: 'pulse 2s ease-in-out infinite'
                }}
              />
            )}
          </span>
          {isExpanded ? (
            <ChevronUp size={16} color={ABLE_COLORS.textMuted} />
          ) : (
            <ChevronDown size={16} color={ABLE_COLORS.textMuted} />
          )}
        </button>

        {/* Pulse animation styles */}
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(1.2); }
          }
        `}</style>
        
        {/* State cards row - only shown when expanded */}
        {isExpanded && (
          <div style={{
            display: 'flex',
            gap: '8px',
            padding: '0 12px 12px 12px'
          }}>
            <StateCard
              icon={moodDisplay.icon}
              label={moodDisplay.label}
              color={moodDisplay.color}
              isSet={moodDisplay.isSet}
              onClick={() => setActiveModal('mood')}
            />
            <StateCard
              icon={energyDisplay.icon}
              label={energyDisplay.label}
              color={energyDisplay.color}
              isSet={energyDisplay.isSet}
              onClick={() => setActiveModal('energy')}
            />
            <StateCard
              icon={focusDisplay.icon}
              label={focusDisplay.label}
              color={focusDisplay.color}
              isSet={focusDisplay.isSet}
              onClick={() => setActiveModal('focus')}
            />
            <StateCard
              icon={spaceDisplay.icon}
              label={spaceDisplay.label}
              color={spaceDisplay.color}
              isSet={spaceDisplay.isSet}
              onClick={() => setActiveModal('space')}
            />
          </div>
        )}
      </div>

      {/* Mood Modal */}
      {activeModal === 'mood' && (
        <MoodModal
          currentMood={currentState.mood}
          onSave={handleMoodChange}
          onClose={() => setActiveModal(null)}
        />
      )}

      {/* Energy Modal */}
      {activeModal === 'energy' && (
        <StateModal
          title="How's your energy?"
          options={ENERGY_OPTIONS}
          selected={currentState.energy}
          multiSelect={false}
          onSave={handleEnergyChange}
          onClose={() => setActiveModal(null)}
        />
      )}

      {/* Focus Modal */}
      {activeModal === 'focus' && (
        <StateModal
          title="What's your focus?"
          options={FOCUS_OPTIONS}
          selected={currentState.intents || (currentState.intent ? [currentState.intent] : [])}
          multiSelect={true}
          onSave={handleFocusChange}
          onClose={() => setActiveModal(null)}
        />
      )}

      {/* Space Modal */}
      {activeModal === 'space' && (
        <StateModal
          title="Where are you?"
          options={SPACE_OPTIONS}
          selected={currentState.location}
          multiSelect={false}
          onSave={handleSpaceChange}
          onClose={() => setActiveModal(null)}
        />
      )}
    </>
  );
}
